import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
} from '@mui/material';
import { PlayArrow, Refresh, Delete, Add } from '@mui/icons-material';
import apiClient from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

interface Admin {
  id: number;
  google_sub: string;
  primary_email: string;
  first_name: string;
  last_name: string;
  role: 'super_admin' | 'admin';
}

const DatabaseQuery: React.FC = () => {
  const { isSuperAdmin } = useAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [clearingCache, setClearingCache] = useState(false);
  const [cacheMessage, setCacheMessage] = useState<string | null>(null);
  
  // Org Report upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadMessage, setUploadMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // Admin management state
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminRole, setNewAdminRole] = useState<'admin' | 'super_admin'>('admin');
  const [adminMessage, setAdminMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [addingAdmin, setAddingAdmin] = useState(false);

  const handleExecuteQuery = async () => {
    if (!query.trim()) {
      setError('Please enter a query');
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const response = await apiClient.post('/api/admin/query', { query });
      setResults(response.data.data || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to execute query');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setUploadMessage(null);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      setUploadMessage({ type: 'error', text: 'Please select a file first' });
      return;
    }

    setUploadingFile(true);
    setUploadMessage(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await apiClient.post('/api/admin/upload-org-report', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadMessage({
        type: 'success',
        text: `File uploaded successfully! ${response.data.data.rows} employees loaded.`
      });
      setSelectedFile(null);
      // Reset file input
      const fileInput = document.getElementById('org-report-file-input') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch (err: any) {
      setUploadMessage({
        type: 'error',
        text: err.response?.data?.error || 'Failed to upload file'
      });
    } finally {
      setUploadingFile(false);
    }
  };

  const handleClearCache = async () => {
    setClearingCache(true);
    setCacheMessage(null);

    try {
      // 1. Clear backend cache
      const response = await apiClient.post('/api/admin/clear-employee-cache');
      
      // 2. Clear frontend cache
      const { clearEmployeeCache } = await import('../utils/employeeData');
      clearEmployeeCache();
      console.log('[DatabaseQuery] Cleared frontend employee cache');
      
      // 3. Clear OrgChart localStorage
      localStorage.removeItem('orgchart_state_v1');
      console.log('[DatabaseQuery] Cleared OrgChart localStorage');
      
      setCacheMessage(response.data.message || 'Cache cleared successfully. Refresh the Org Chart page to see updated data.');
    } catch (err: any) {
      setCacheMessage(err.response?.data?.error || 'Failed to clear cache');
    } finally {
      setClearingCache(false);
    }
  };

  const loadAdmins = async () => {
    if (!isSuperAdmin) return;
    
    setLoadingAdmins(true);
    try {
      const response = await apiClient.get('/api/admin/admins');
      setAdmins(response.data.data || []);
    } catch (err: any) {
      console.error('Error loading admins:', err);
    } finally {
      setLoadingAdmins(false);
    }
  };

  const handleAddAdmin = async () => {
    if (!newAdminEmail.trim()) {
      setAdminMessage({ type: 'error', text: 'Please enter an email address' });
      return;
    }

    setAddingAdmin(true);
    setAdminMessage(null);

    try {
      const response = await apiClient.post('/api/admin/admins', {
        email: newAdminEmail,
        role: newAdminRole,
      });
      setAdminMessage({ type: 'success', text: response.data.message || 'Admin added successfully' });
      setNewAdminEmail('');
      setNewAdminRole('admin');
      loadAdmins();
    } catch (err: any) {
      setAdminMessage({ 
        type: 'error', 
        text: err.response?.data?.error || 'Failed to add admin' 
      });
    } finally {
      setAddingAdmin(false);
    }
  };

  const handleRemoveAdmin = async (userId: number, role: string) => {
    if (!window.confirm(`Remove ${role} role from this user?`)) {
      return;
    }

    try {
      await apiClient.delete(`/api/admin/admins/${userId}`, {
        data: { role }
      });
      setAdminMessage({ type: 'success', text: 'Admin role removed successfully' });
      loadAdmins();
    } catch (err: any) {
      setAdminMessage({ 
        type: 'error', 
        text: err.response?.data?.error || 'Failed to remove admin' 
      });
    }
  };

  useEffect(() => {
    if (isSuperAdmin) {
      loadAdmins();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  const renderTable = () => {
    if (!results || results.length === 0) {
      return <Alert severity="info">No results returned</Alert>;
    }

    const columns = Object.keys(results[0]);

    return (
      <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col} sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {results.map((row, idx) => (
              <TableRow key={idx}>
                {columns.map((col) => (
                  <TableCell key={col}>
                    {row[col] === null ? <em>null</em> : String(row[col])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Admin Tools
      </Typography>
      <Alert severity="warning" sx={{ mb: 3 }}>
        <strong>Warning:</strong> This tool runs queries directly against the production database.
        Use with caution.
      </Alert>

      {/* Admin Management Section - Super Admins Only */}
      {isSuperAdmin && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Admin Management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add or remove admin users. Super admins have full access including this admin panel.
            Regular admins can see all vendor records and delete them, but cannot manage other admins.
          </Typography>

          {/* Add Admin Form */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-end' }}>
            <TextField
              label="Email Address"
              value={newAdminEmail}
              onChange={(e) => setNewAdminEmail(e.target.value)}
              placeholder="user@angel.com"
              sx={{ flex: 1 }}
            />
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={newAdminRole}
                label="Role"
                onChange={(e) => setNewAdminRole(e.target.value as 'admin' | 'super_admin')}
              >
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="super_admin">Super Admin</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              startIcon={addingAdmin ? <CircularProgress size={20} /> : <Add />}
              onClick={handleAddAdmin}
              disabled={addingAdmin}
            >
              Add Admin
            </Button>
          </Box>

          {adminMessage && (
            <Alert severity={adminMessage.type} sx={{ mb: 2 }}>
              {adminMessage.text}
            </Alert>
          )}

          {/* Current Admins List */}
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Current Admins
          </Typography>
          {loadingAdmins ? (
            <CircularProgress size={24} />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Email</strong></TableCell>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Role</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {admins.map((admin) => (
                    <TableRow key={admin.id}>
                      <TableCell>{admin.primary_email || 'N/A'}</TableCell>
                      <TableCell>
                        {admin.first_name && admin.last_name 
                          ? `${admin.first_name} ${admin.last_name}` 
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={admin.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                          color={admin.role === 'super_admin' ? 'primary' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveAdmin(admin.id, admin.role)}
                          title="Remove admin role"
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Org Chart Update Section - Available to all Admins */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Org Chart Update
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload a new Org Report.xlsx file to update employee data.
        </Typography>
        
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Important:</strong> The uploaded file must be in the same format as all previous versions 
          (same column names and structure). Only .xlsx and .xls files are accepted.
        </Alert>

        {/* File Upload Section */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
          <input
            type="file"
            id="org-report-file-input"
            accept=".xlsx,.xls"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <label htmlFor="org-report-file-input">
            <Button variant="outlined" component="span">
              Choose File
            </Button>
          </label>
          {selectedFile && (
            <Typography variant="body2" color="text.secondary">
              {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
            </Typography>
          )}
          <Button
            variant="contained"
            onClick={handleFileUpload}
            disabled={!selectedFile || uploadingFile}
            startIcon={uploadingFile ? <CircularProgress size={20} /> : <Add />}
          >
            {uploadingFile ? 'Uploading...' : 'Upload File'}
          </Button>
        </Box>

        {uploadMessage && (
          <Alert severity={uploadMessage.type} sx={{ mb: 2 }}>
            {uploadMessage.text}
          </Alert>
        )}

        {/* Clear Cache Section */}
        <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e0e0e0' }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Clear Employee Cache
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            After uploading a new file, clear the cache to reload employee data throughout the application.
          </Typography>
          <Button
            variant="outlined"
            startIcon={clearingCache ? <CircularProgress size={20} /> : <Refresh />}
            onClick={handleClearCache}
            disabled={clearingCache}
          >
            Clear Employee Cache
          </Button>
          {cacheMessage && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {cacheMessage}
            </Alert>
          )}
        </Box>
      </Paper>

      {/* Database Query Section - Super Admin Only */}
      {isSuperAdmin && (
        <>
          <Typography variant="h6" gutterBottom>
            Database Query Tool
          </Typography>

          <Paper sx={{ p: 3, mb: 3 }}>
        <TextField
          fullWidth
          multiline
          rows={8}
          label="SQL Query"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          variant="outlined"
          placeholder="SELECT * FROM supplier_due_diligence LIMIT 10;"
          sx={{ mb: 2, fontFamily: 'monospace' }}
        />
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <PlayArrow />}
          onClick={handleExecuteQuery}
          disabled={loading}
        >
          Execute Query
        </Button>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {results && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Results ({results.length} rows)
          </Typography>
          {renderTable()}
        </Box>
      )}
        </>
      )}
    </Box>
  );
};

export default DatabaseQuery;
